/**
 * 基础配置 - 系统配置 接口定义
 */
import { apiGet, apiPut } from '@/utils/request';

/** 离线后访客去向枚举 */
export const AGENT_OFFLINE_VISITOR_ROUTING = {
  /** 继续寻找其他客服 */
  CONTINUE_SEARCH: 'continue_search',
  /** 进入排队等待 */
  QUEUE_WAIT: 'queue_wait',
  /** 转接人工客服 */
  TRANSFER_TO_HUMAN: 'transfer_to_human',
} as const;

export type AgentOfflineVisitorRouting =
  (typeof AGENT_OFFLINE_VISITOR_ROUTING)[keyof typeof AGENT_OFFLINE_VISITOR_ROUTING];

/** 客资定义可选值 */
export type CustomerAssetDefKey = 'mobile' | 'wechat' | 'wechat_qrcode' | 'wechat_copy';

/** 列表标签展示类型可选值（系统配置 / 自定义） */
export const LIST_TAG_TYPE_OPTIONS = [
  { value: 'system', label: '系统配置' },
  { value: 'custom', label: '自定义' },
] as const;

/** 扩展配置（config.extra_config，字段可选，不传走后端默认） */
export interface SystemConfigExtra {
  /** 访客会话列表展示标签类型 system/custom */
  visitor_session_tag_type?: string[];
  /** 聊天记录列表展示标签类型 system/custom */
  chat_record_tag_type?: string[];
  /** 是否启用无效用户续跟 0=否 1=是 */
  invalid_user_follow?: number;
}

/** 系统配置项（与更新接口 config 结构一致） */
export interface SystemConfigItem {
  /** 客资定义 mobile/wechat/wechat_qrcode/wechat_copy */
  customer_asset_def?: string[];
  /** 客资地址补全-使用手机号IP补全 0/1 */
  customer_asset_address_auto?: number;
  /** 离线后访客去向 */
  agent_offline_visitor_routing?: string;
  /** 访客分配坐席后弹窗提醒 0/1 */
  agent_assign_popup_remind?: number;
  /** 访客分配坐席后提示音提醒 0/1 */
  agent_assign_sound_remind?: number;
  /** 客服退出系统后自动标记为离线 0/1 */
  agent_auto_offline_on_exit?: number;
  /** 客服未操作小时数算离线 */
  agent_inactive_hours?: number;
  /** 访客未发言自动结束会话小时数 */
  session_auto_close_hours?: number;
  /** 访客未发言小时数自动结束会话，0表示不自动结束 */
  visitor_inactive_hours?: number;
  /** 未留资访客超过天数算新客户 */
  visitor_new_customer_days?: number;
  /** 扩展配置 */
  extra_config?: SystemConfigExtra;
}

/** 系统配置获取响应 GET /api/system-config */
export interface SystemConfigResponse {
  id?: string | number;
  config?: SystemConfigItem;
  /** 兼容扁平结构：config 字段可能直接展开在根级 */
  customer_asset_def?: string[];
  customer_asset_address_auto?: number;
  agent_offline_visitor_routing?: string;
  agent_assign_popup_remind?: number;
  agent_assign_sound_remind?: number;
  agent_auto_offline_on_exit?: number;
  agent_inactive_hours?: number;
  session_auto_close_hours?: number;
  visitor_new_customer_days?: number;
}

/** 系统配置更新请求体 */
export interface SystemConfigUpdateBody {
  config: {
    /** 客资定义 */
    customer_asset_def: string[];
    /** 客资地址补全-使用手机号IP补全 0/1 */
    customer_asset_address_auto: number;
    /** 离线后访客去向 continue_search/queue_wait/transfer_to_human */
    agent_offline_visitor_routing: string;
    /** 访客分配坐席后弹窗提醒 0/1 */
    agent_assign_popup_remind: number;
    /** 访客分配坐席后提示音提醒 0/1 */
    agent_assign_sound_remind: number;
    /** 客服退出系统后自动标记为离线 0/1 */
    agent_auto_offline_on_exit: number;
    /** 客服未操作小时数算离线 */
    agent_inactive_hours: number;
    /** 访客未发言小时数自动结束会话，0表示不自动结束 */
    visitor_inactive_hours: number;
    /** 未留资访客超过天数算新客户 */
    visitor_new_customer_days: number;
    /** 扩展配置（列表标签展示类型等） */
    extra_config: SystemConfigExtra;
  };
}

export const systemConfigApi = {
  /** 获取系统配置 GET /api/system-config */
  get: () => apiGet<SystemConfigResponse>('/system-config'),
  /** 更新系统配置 PUT /api/system-config */
  update: (body: SystemConfigUpdateBody) => apiPut(`/system-config`, body),
};
